/**
 * @description
 * Returns a CSS variable value for a color definition
 * @descriptionRu
 * Возвращает значение CSS-переменной для определения цвета
 * @param colorDefinition
 * @param fallback - fallback value if colorDefinition is not provided
 */
export const cssVariable = (
  colorDefinition: DesignSystemColorDefinition,
  fallback = 'inherit'
): string => {
  return colorDefinition ? `var(${colorDefinition.cssVariable})` : fallback
}

export interface DesignSystemColorDefinition {
  name: string
  value: {
    light: string
    dark: string
  }
  cssVariable: string
  _tailwindColorsAddress: string
}

export type TColorGroupNames =
  | 'brand'
  | 'text'
  | 'icon'
  | 'border'
  | 'backgroundPrimary'
  | 'backgroundSecondary'
  | 'backgroundSuccess'
  | 'backgroundAttention'
  | 'backgroundOther'
  | 'backgroundOpacityWhite'
  | 'backgroundOpacityMiddle'
  | 'backgroundOpacityDark'
  | 'notification'
  | 'status'
  | 'accentTintsNormal'
  | 'accentTintsAcid'
  | 'elementsTaskProgress'
  | 'elementsTaskCard'
  | 'elementsColumnBg'
  | 'elementsStickerIcon'
  | 'elementsStickerIconBg'
  | 'elementsStickerText'
  | 'elementsStickerBg'
  | 'elementsPanelText'
  | 'elementsPanelIcon'
  | 'elementsPanelBorder'
  | 'elementsPanelBg'
  | 'elementsChatInverse'
  | 'elementsChatReactionOutcoming'
  | 'elementsChatReactionIncoming'
  | 'elementsChatBubble'
  | 'scroll'
  | 'opacity'
  | 'overlay'

export type SharedColors = Record<
  TColorGroupNames,
  {
    [nestedKey: string]: DesignSystemColorDefinition
  }
>

/**
 * @description
 * Collection of colors of DS
 * @descriptionRu
 * Коллекция цветов DS
 */
export const sharedColors = {
  // 1) Brand
  brand: {
    default: {
      name: 'Colors/Brand/Default',
      value: {
        light: '#3F8BFD',
        dark: '#3F8BFD'
      },
      cssVariable: '--color-primary-action-default',
      _tailwindColorsAddress: 'primary.action-default'
    },
    hover: {
      name: 'Colors/Brand/Hover',
      value: {
        light: '#5E9EFF',
        dark: '#5E9EFF'
      },
      cssVariable: '--color-primary-action-hover',
      _tailwindColorsAddress: 'primary.action-hover'
    },
    pressed: {
      name: 'Colors/Brand/Pressed',
      value: {
        light: '#3175D7',
        dark: '#3175D7'
      },
      cssVariable: '--color-primary-action-pressed',
      _tailwindColorsAddress: 'primary.action-pressed'
    },
    disabled: {
      name: 'Colors/Brand/Disabled',
      value: {
        light: '#F4F5F9',
        dark: '#2E3542'
      },
      cssVariable: '--color-primary-action-disabled',
      _tailwindColorsAddress: 'primary.action-disabled'
    }
  },

  // 2) Text
  text: {
    primary: {
      name: 'Colors/Text/Primary',
      value: {
        light: '#121B2E',
        dark: '#E7EBF1'
      },
      cssVariable: '--color-text-primary',
      _tailwindColorsAddress: 'text.primary'
    },
    secondary: {
      name: 'Colors/Text/Secondary',
      value: {
        light: '#667085',
        dark: '#98A2B3'
      },
      cssVariable: '--color-text-secondary',
      _tailwindColorsAddress: 'text.secondary'
    },
    placeholder: {
      name: 'Colors/Text/Placeholder',
      value: {
        light: '#B1BACD',
        dark: '#667085'
      },
      cssVariable: '--color-text-placeholder-new',
      _tailwindColorsAddress: 'text.placeholder-new'
    },
    disabled: {
      name: 'Colors/Text/Disabled',
      value: {
        light: '#D0D5DD',
        dark: '#2D343E'
      },
      cssVariable: '--color-text-disabled',
      _tailwindColorsAddress: 'text.disabled'
    },
    fixed: {
      name: 'Colors/Text/Fixed',
      value: {
        light: '#FFFFFF',
        dark: '#FFFFFF'
      },
      cssVariable: '--color-text-invert',
      _tailwindColorsAddress: 'text.invert'
    },
    opacity64: {
      name: 'Colors/Text/Opacity64',
      value: {
        light: 'rgba(18,27,46,0.64)',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    }
  },

  // 3) Icon
  icon: {
    primary: {
      name: 'Colors/Icon/Primary',
      value: {
        light: '#121B2E',
        dark: '#E7EBF1'
      },
      cssVariable: '--color-primary-icon-primary',
      _tailwindColorsAddress: 'primary.icon-primary'
    },
    secondary: {
      name: 'Colors/Icon/Secondary',
      value: {
        light: '#667085',
        dark: '#98A2B3'
      },
      cssVariable: '--color-primary-icon-secondary',
      _tailwindColorsAddress: 'primary.icon-secondary'
    },
    placeholder: {
      name: 'Colors/Icon/Placeholder',
      value: {
        light: '#B1BACD',
        dark: '#667085'
      },
      cssVariable: '--color-primary-icon-placeholder',
      _tailwindColorsAddress: '[MISSED]' // в allColors нет "icon-placeholder"
    },
    disabled: {
      name: 'Colors/Icon/Disabled',
      value: {
        light: '#D0D5DD',
        dark: '#546072'
      },
      cssVariable: '--color-primary-icon-disabled',
      _tailwindColorsAddress: 'primary.icon-disabled'
    },
    fixed: {
      name: 'Colors/Icon/Fixed',
      value: {
        light: '#FFFFFF',
        dark: '#FFFFFF'
      },
      cssVariable: '--color-primary-icon-invert',
      _tailwindColorsAddress: 'primary.icon-invert'
    },
    opacity64: {
      name: 'Colors/Icon/Opacity64',
      value: {
        light: 'rgba(18,27,46,0.64)',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    }
  },

  // 4) Border
  border: {
    base: {
      name: 'Colors/Border/Base',
      value: {
        light: '#E3E5EF',
        dark: '#384150'
      },
      cssVariable: '--color-primary-border-base',
      _tailwindColorsAddress: 'primary.border-base'
    },
    hover: {
      name: 'Colors/Border/Hover',
      value: {
        light: '#CFD6E2',
        dark: '#424D5F'
      },
      cssVariable: '--color-primary-line-hover',
      _tailwindColorsAddress: 'primary.line-hover'
    },
    pressed: {
      name: 'Colors/Border/Pressed',
      value: {
        light: '#B1BACD',
        dark: '#667085'
      },
      cssVariable: '--color-primary-line-pressed',
      _tailwindColorsAddress: 'primary.line-pressed'
    },
    disabled: {
      name: 'Colors/Border/Disabled',
      value: {
        light: '#F4F5F9',
        dark: '#2E3542'
      },
      cssVariable: '--color-primary-line-disabled',
      _tailwindColorsAddress: 'primary.line-disabled'
    },
    invert: {
      name: 'Colors/Border/Invert',
      value: {
        light: '#FFFFFF',
        dark: '#252B35'
      },
      cssVariable: '--color-primary-line-invert',
      _tailwindColorsAddress: 'primary.line-invert'
    }
  },

  // 5) BackgroundPrimary
  backgroundPrimary: {
    default: {
      name: 'Colors/BackgroundPrimary/Default',
      value: {
        light: '#FFFFFF',
        dark: '#252B35'
      },
      cssVariable: '--color-primary-background-primary',
      _tailwindColorsAddress: 'primary.background-primary'
    },
    hover: {
      name: 'Colors/BackgroundPrimary/Hover',
      value: {
        light: '#F7F8FD',
        dark: '#29313D'
      },
      cssVariable: '--color-primary-background-hover',
      _tailwindColorsAddress: 'primary.background-hover'
    },
    pressed: {
      name: 'Colors/BackgroundPrimary/Pressed',
      value: {
        light: '#EFF0F6',
        dark: '#333C49'
      },
      cssVariable: '--color-primary-background-active',
      _tailwindColorsAddress: 'primary.background-active'
    },
    disabled: {
      name: 'Colors/BackgroundPrimary/Disabled',
      value: {
        light: '#F4F5F9',
        dark: '#2E3542'
      },
      cssVariable: '--color-primary-background-disabled',
      _tailwindColorsAddress: 'primary.background-disabled'
    }
  },

  // 6) BackgroundSecondary
  backgroundSecondary: {
    default: {
      name: 'Colors/BackgroundSecondary/Default',
      value: {
        light: '#EFF0F6',
        dark: '#20252C'
      },
      cssVariable: '--color-primary-background-secondary',
      _tailwindColorsAddress: 'primary.background-secondary'
    }
  },

  // 7) BackgroundSuccess
  backgroundSuccess: {
    default: {
      name: 'Colors/BackgroundSuccess/Default',
      value: {
        light: '#80C856',
        dark: '#8ECE69'
      },
      cssVariable: '--color-primary-success',
      _tailwindColorsAddress: 'primary.success'
    },
    hover: {
      name: 'Colors/BackgroundSuccess/Hover',
      value: {
        light: '#B6FC6A', // нет в tailwind-colors
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    },
    pressed: {
      name: 'Colors/BackgroundSuccess/Pressed',
      value: {
        light: '#71B04C',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    },
    disabled: {
      name: 'Colors/BackgroundSuccess/Disabled',
      value: {
        light: '#F4F5F9',
        dark: '#2E3542'
      },
      cssVariable: '--color-primary-background-disabled',
      _tailwindColorsAddress: 'primary.background-disabled'
    }
  },

  // 8) BackgroundAttention
  backgroundAttention: {
    default: {
      name: 'Colors/BackgroundAttention/Default',
      value: {
        light: '#FF6666',
        dark: '#FF6666'
      },
      cssVariable: '--color-primary-action-attention-default',
      _tailwindColorsAddress: '[MISSED]'
    },
    hover: {
      name: 'Colors/BackgroundAttention/Hover',
      value: {
        light: '#FF7E7E',
        dark: '#FF7E7E'
      },
      cssVariable: '--color-primary-action-attention-hover',
      _tailwindColorsAddress: '[MISSED]'
    },
    pressed: {
      name: 'Colors/BackgroundAttention/Pressed',
      value: {
        light: '#EF5D5D',
        dark: '#EF5D5D'
      },
      cssVariable: '--color-primary-action-attention-pressed',
      _tailwindColorsAddress: '[MISSED]'
    },
    disabled: {
      name: 'Colors/BackgroundAttention/Disabled',
      value: {
        light: '#F4F5F9',
        dark: '#2E3542'
      },
      cssVariable: '--color-primary-background-disabled',
      _tailwindColorsAddress: 'primary.background-disabled'
    }
  },

  // 9) BackgroundOther
  backgroundOther: {
    grayRow: {
      name: 'Colors/BackgroundOther/GrayRow',
      value: {
        light: '#F4F5F9',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    },
    subtaskOpen: {
      name: 'Colors/BackgroundOther/SubtaskOpen',
      value: {
        light: '#E3E5EF',
        dark: '#1E222B'
      },
      cssVariable: '--color-primary-background-subtask-open',
      _tailwindColorsAddress: 'primary.background-subtask-open'
    },
    stickerPanel: {
      name: 'Colors/BackgroundOther/StickerPanel',
      value: {
        light: '#E3E4EE',
        dark: '#2D333C'
      },
      cssVariable: '--color-primary-background-sticker-panel',
      _tailwindColorsAddress: 'primary.background-sticker-panel'
    },
    solidBg: {
      name: 'Colors/BackgroundOther/SolidBg',
      value: {
        light: '#FFFFFF',
        dark: '#1C222C'
      },
      cssVariable: '--color-primary-background-solid',
      _tailwindColorsAddress: 'primary.background-solid'
    },
    tooltipInformer: {
      name: 'Colors/BackgroundOther/TooltipInformer',
      value: {
        light: '#344054',
        dark: '#344054'
      },
      cssVariable: '--color-primary-background-tooltip-informer',
      _tailwindColorsAddress: '[MISSED]'
    }
  },

  // 10) BackgroundOpacityWhite
  backgroundOpacityWhite: {
    default: {
      name: 'Colors/BackgroundOpacityWhite/Default',
      value: {
        light: 'rgba(255,255,255,0.4)',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    },
    hover: {
      name: 'Colors/BackgroundOpacityWhite/Hover',
      value: {
        light: 'rgba(255,255,255,0.5)',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    },
    pressed: {
      name: 'Colors/BackgroundOpacityWhite/Pressed',
      value: {
        light: 'rgba(255,255,255,0.8)',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    }
  },

  // 11) BackgroundOpacityMiddle
  backgroundOpacityMiddle: {
    hover: {
      name: 'Colors/BackgroundOpacityMiddle/Hover',
      value: {
        light: 'rgba(18,27,46,0.08)',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    },
    pressed: {
      name: 'Colors/BackgroundOpacityMiddle/Pressed',
      value: {
        light: 'rgba(18,27,46,0.16)',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    }
  },

  // 12) BackgroundOpacityDark
  backgroundOpacityDark: {
    default: {
      name: 'Colors/BackgroundOpacityDark/Default',
      value: {
        light: 'rgba(18,27,46,0.40)',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    },
    hover: {
      name: 'Colors/BackgroundOpacityDark/Hover',
      value: {
        light: 'rgba(18,27,46,0.60)',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    },
    pressed: {
      name: 'Colors/BackgroundOpacityDark/Pressed',
      value: {
        light: 'rgba(18,27,46,0.80)',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    }
  },

  // 13) Notification
  notification: {
    primary: {
      name: 'Colors/Notification/Primary',
      value: {
        light: '#FF6666',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: 'primary.notification-primary'
    }
  },

  // 14) Status
  status: {
    success: {
      name: 'Colors/Status/Success',
      value: {
        light: '#80C856',
        dark: '#73B24E'
      },
      cssVariable: '--color-primary-status-success',
      _tailwindColorsAddress: 'primary.status-success'
    },
    warning: {
      name: 'Colors/Status/Warning',
      value: {
        light: '#FF902B',
        dark: '#EA8D38'
      },
      cssVariable: '--color-primary-status-warning',
      _tailwindColorsAddress: 'primary.status-warning'
    },
    error: {
      name: 'Colors/Status/Error',
      value: {
        light: '#FF6666',
        dark: '#DF5050'
      },
      cssVariable: '--color-primary-status-error',
      _tailwindColorsAddress: 'primary.status-error'
    }
  },

  // 15) Accent Tints (Normal)
  accentTintsNormal: {
    gray: {
      name: 'Colors/AccentTintsNormal/Gray',
      value: {
        light: '#B1BACD',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: 'primary.normal-gray'
    },
    red: {
      name: 'Colors/AccentTintsNormal/Red',
      value: {
        light: '#F79F79',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: 'primary.normal-red'
    },
    pink: {
      name: 'Colors/AccentTintsNormal/Pink',
      value: {
        light: '#F18CC3',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: 'primary.normal-pink'
    },
    orange: {
      name: 'Colors/AccentTintsNormal/Orange',
      value: {
        light: '#F2A24F',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: 'primary.normal-orange'
    },
    yellow: {
      name: 'Colors/AccentTintsNormal/Yellow',
      value: {
        light: '#FCE258',
        dark: '#FCE258'
      },
      cssVariable: '--color-primary-normal-yellow',
      _tailwindColorsAddress: 'primary.normal-yellow'
    },
    green: {
      name: 'Colors/AccentTintsNormal/Green',
      value: {
        light: '#78BC55',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: 'primary.normal-green'
    },
    turquoise: {
      name: 'Colors/AccentTintsNormal/Turquoise',
      value: {
        light: '#49C5BC',
        dark: '#54A09B'
      },
      cssVariable: '--color-primary-normal-turquoise',
      _tailwindColorsAddress: 'primary.normal-turquoise'
    },
    blue: {
      name: 'Colors/AccentTintsNormal/Blue',
      value: {
        light: '#8CACFF',
        dark: '#6B86CC'
      },
      cssVariable: '--color-primary-normal-blue',
      _tailwindColorsAddress: 'primary.normal-blue'
    },
    violet: {
      name: 'Colors/AccentTintsNormal/Violet',
      value: {
        light: '#DABCF1',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: 'primary.normal-violet'
    }
  },

  // 16) Accent Tints (Acid)
  accentTintsAcid: {
    gray: {
      name: 'Colors/AccentTintsAcid/Gray',
      value: {
        light: '#667085',
        dark: '#667085'
      },
      cssVariable: '--color-primary-acid-gray',
      _tailwindColorsAddress: 'primary.acid-gray'
    },
    red: {
      name: 'Colors/AccentTintsAcid/Red',
      value: {
        light: '#EB3737',
        dark: '#DB3C3C'
      },
      cssVariable: '--color-primary-acid-red',
      _tailwindColorsAddress: 'primary.acid-red'
    },
    pink: {
      name: 'Colors/AccentTintsAcid/Pink',
      value: {
        light: '#FD57A7',
        dark: '#EF5585'
      },
      cssVariable: '--color-primary-acid-pink',
      _tailwindColorsAddress: 'primary.acid-pink'
    },
    orange: {
      name: 'Colors/AccentTintsAcid/Orange',
      value: {
        light: '#F2732B',
        dark: '#D86727'
      },
      cssVariable: '--color-primary-acid-orange',
      _tailwindColorsAddress: 'primary.acid-orange'
    },
    yellow: {
      name: 'Colors/AccentTintsAcid/Yellow',
      value: {
        light: '#F5CC00',
        dark: '#D4B102'
      },
      cssVariable: '--color-primary-acid-yellow',
      _tailwindColorsAddress: 'primary.acid-yellow'
    },
    green: {
      name: 'Colors/AccentTintsAcid/Green',
      value: {
        light: '#5CDC11',
        dark: '#58B522'
      },
      cssVariable: '--color-primary-acid-green',
      _tailwindColorsAddress: 'primary.acid-green'
    },
    turquoise: {
      name: 'Colors/AccentTintsAcid/Turquoise',
      value: {
        light: '#08A7A9',
        dark: '#08A7A9'
      },
      cssVariable: '--color-primary-acid-turquoise',
      _tailwindColorsAddress: 'primary.acid-turquoise'
    },
    blue: {
      name: 'Colors/AccentTintsAcid/Blue',
      value: {
        light: '#5089F2',
        dark: '#467DE1'
      },
      cssVariable: '--color-primary-acid-blue',
      _tailwindColorsAddress: 'primary.acid-blue'
    },
    violet: {
      name: 'Colors/AccentTintsAcid/Violet',
      value: {
        light: '#E25EF2',
        dark: '#BF48CD'
      },
      cssVariable: '--color-primary-acid-violet',
      _tailwindColorsAddress: 'primary.acid-violet'
    }
  },

  // 17) Elements_Task_Progress
  elementsTaskProgress: {
    default: {
      name: 'Colors/ElementsTaskProgress/Default',
      value: {
        light: '#B1BACD',
        dark: '#667085'
      },
      cssVariable: '--color-background-progress-task-primary',
      _tailwindColorsAddress: 'primary.progress-task-primary'
    },
    gray: {
      name: 'Colors/ElementsTaskProgress/Gray',
      value: {
        light: '#B1BACD',
        dark: '#576273'
      },
      cssVariable: '--color-background-progress-task-gray',
      _tailwindColorsAddress: 'primary.progress-task-gray'
    },
    red: {
      name: 'Colors/ElementsTaskProgress/Red',
      value: {
        light: '#DD8686',
        dark: '#8C5A5A'
      },
      cssVariable: '--color-background-progress-task-red',
      _tailwindColorsAddress: 'primary.progress-task-red'
    },
    pink: {
      name: 'Colors/ElementsTaskProgress/Pink',
      value: {
        light: '#E3A3B2',
        dark: '#94597D'
      },
      cssVariable: '--color-background-progress-task-pink',
      _tailwindColorsAddress: 'primary.progress-task-pink'
    },
    yellow: {
      name: 'Colors/ElementsTaskProgress/Yellow',
      value: {
        light: '#E3D08A',
        dark: '#90814D'
      },
      cssVariable: '--color-background-progress-task-yellow',
      _tailwindColorsAddress: 'primary.progress-task-yellow'
    },
    green: {
      name: 'Colors/ElementsTaskProgress/Green',
      value: {
        light: '#B3DE99',
        dark: '#537B4C'
      },
      cssVariable: '--color-background-progress-task-green',
      _tailwindColorsAddress: 'primary.progress-task-green'
    },
    turquoise: {
      name: 'Colors/ElementsTaskProgress/Turquoise',
      value: {
        light: '#A0D7CD',
        dark: '#4A7E75'
      },
      cssVariable: '--color-background-progress-task-turquoise',
      _tailwindColorsAddress: 'primary.progress-task-turquoise'
    },
    blue: {
      name: 'Colors/ElementsTaskProgress/Blue',
      value: {
        light: '#A1C1E6',
        dark: '#47678D'
      },
      cssVariable: '--color-background-progress-task-blue',
      _tailwindColorsAddress: 'primary.progress-task-blue'
    },
    violet: {
      name: 'Colors/ElementsTaskProgress/Violet',
      value: {
        light: '#D79BDC',
        dark: '#7E5D9E'
      },
      cssVariable: '--color-background-progress-task-violet',
      _tailwindColorsAddress: 'primary.progress-task-violet'
    }
  },

  // 18) Elements_Task_Card
  elementsTaskCard: {
    default: {
      name: 'Colors/ElementsTaskCard/Default',
      value: {
        light: '#FFFFFF',
        dark: '#333C49'
      },
      cssVariable: '--color-background-task-primary',
      _tailwindColorsAddress: 'primary.task-primary'
    },
    gray: {
      name: 'Colors/ElementsTaskCard/Gray',
      value: {
        light: '#DDE1EA',
        dark: '#424D5F'
      },
      cssVariable: '--color-background-task-gray',
      _tailwindColorsAddress: 'primary.task-gray'
    },
    red: {
      name: 'Colors/ElementsTaskCard/Red',
      value: {
        light: '#FFB0B0',
        dark: '#703535'
      },
      cssVariable: '--color-background-task-red',
      _tailwindColorsAddress: 'primary.task-red'
    },
    pink: {
      name: 'Colors/ElementsTaskCard/Pink',
      value: {
        light: '#FFD1D6',
        dark: '#713C4F'
      },
      cssVariable: '--color-background-task-pink',
      _tailwindColorsAddress: 'primary.task-pink'
    },
    yellow: {
      name: 'Colors/ElementsTaskCard/Yellow',
      value: {
        light: '#FFF7AD',
        dark: '#706335'
      },
      cssVariable: '--color-background-task-yellow',
      _tailwindColorsAddress: 'primary.task-yellow'
    },
    green: {
      name: 'Colors/ElementsTaskCard/Green',
      value: {
        light: '#DFFABC',
        dark: '#34572E'
      },
      cssVariable: '--color-background-task-green',
      _tailwindColorsAddress: 'primary.task-green'
    },
    turquoise: {
      name: 'Colors/ElementsTaskCard/Turquoise',
      value: {
        light: '#C8F7E6',
        dark: '#2A5D54'
      },
      cssVariable: '--color-background-task-turquoise',
      _tailwindColorsAddress: 'primary.task-turquoise'
    },
    blue: {
      name: 'Colors/ElementsTaskCard/Blue',
      value: {
        light: '#BEE1FB',
        dark: '#304D6F'
      },
      cssVariable: '--color-background-task-blue',
      _tailwindColorsAddress: 'primary.task-blue'
    },
    violet: {
      name: 'Colors/ElementsTaskCard/Violet',
      value: {
        light: '#F9C9F7',
        dark: '#673A76'
      },
      cssVariable: '--color-background-task-violet',
      _tailwindColorsAddress: 'primary.task-violet'
    }
  },

  // 19) Elements_Column_BG
  elementsColumnBg: {
    gray: {
      name: 'Colors/ElementsColumnBg/Gray',
      value: {light: '#E3E4EE', dark: '[MISSED]'},
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    },
    red: {
      name: 'Colors/ElementsColumnBg/Red',
      value: {light: '#F0CECE', dark: '[MISSED]'},
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    },
    pink: {
      name: 'Colors/ElementsColumnBg/Pink',
      value: {light: '#E7E0EC', dark: '[MISSED]'},
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    },
    orange: {
      name: 'Colors/ElementsColumnBg/Orange',
      value: {light: '#ECE7D0', dark: '[MISSED]'},
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    }
    // ... etc. (остальные пастельные цвета аналогично)
  },

  // 20) Elements_Sticker_Icon
  elementsStickerIcon: {
    gray: {
      name: 'Colors/ElementsStickerIcon/Gray',
      value: {light: '#FFFFFF', dark: '[MISSED]'},
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    },
    red: {
      name: 'Colors/ElementsStickerIcon/Red',
      value: {light: '#FFFFFF', dark: '[MISSED]'},
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    }
    // ... pink, orange, green, turquoise, blue, violet ...
  },

  // 21) Elements_Sticker_Icon_BG
  elementsStickerIconBg: {
    gray: {
      name: 'Colors/ElementsStickerIconBg/Gray',
      value: {light: '#98A2B3', dark: '[MISSED]'},
      cssVariable: '--color-background-icon-sticker-gray',
      _tailwindColorsAddress: 'background.icon-sticker-gray'
    },
    red: {
      name: 'Colors/ElementsStickerIconBg/Red',
      value: {light: '#D92400', dark: '[MISSED]'},
      cssVariable: '--color-background-icon-sticker-red',
      _tailwindColorsAddress: 'background.icon-sticker-red'
    },
    pink: {
      name: 'Colors/ElementsStickerIconBg/Pink',
      value: {light: '#FF8C8C', dark: '[MISSED]'},
      cssVariable: '--color-background-icon-sticker-pink',
      _tailwindColorsAddress: 'background.icon-sticker-pink'
    }
    // ... и т.д. ...
  },

  // 22) Elements_Sticker_Text
  elementsStickerText: {
    gray: {
      name: 'Colors/ElementsStickerText/Gray',
      value: {light: '#5C677E', dark: '[MISSED]'},
      cssVariable: '--color-text-sticker-gray',
      _tailwindColorsAddress: 'text.sticker-gray'
    },
    red: {
      name: 'Colors/ElementsStickerText/Red',
      value: {light: '#821600', dark: '[MISSED]'},
      cssVariable: '--color-text-sticker-red',
      _tailwindColorsAddress: 'text.sticker-red'
    }
    // ... etc. ...
  },

  // 23) Elements_Sticker_BG
  elementsStickerBg: {
    gray: {
      name: 'Colors/ElementsStickerBg/Gray',
      value: {light: '#F1F2F5', dark: '[MISSED]'},
      cssVariable: '--color-background-sticker-gray',
      _tailwindColorsAddress: 'background.sticker-gray'
    },
    red: {
      name: 'Colors/ElementsStickerBg/Red',
      value: {light: '#F7D3CC', dark: '[MISSED]'},
      cssVariable: '--color-background-sticker-red',
      _tailwindColorsAddress: 'background.sticker-red'
    }
    // ... и т.д. ...
  },

  // 24) Elements_Panel_Text
  elementsPanelText: {
    primary: {
      name: 'Colors/ElementsPanelText/Primary',
      value: {
        light: '#121B2E',
        dark: '#E7EBF1'
      },
      cssVariable: '--color-primary-panel-text-primary',
      _tailwindColorsAddress: 'panel-text-primary'
    },
    secondary: {
      name: 'Colors/ElementsPanelText/Secondary',
      value: {
        light: '#667085',
        dark: '#98A2B3'
      },
      cssVariable: '--color-primary-panel-text-secondary',
      _tailwindColorsAddress: 'panel-text-secondary'
    },
    placeholder: {
      name: 'Colors/ElementsPanelText/Placeholder',
      value: {
        light: '#B1BACD',
        dark: '#667085'
      },
      cssVariable: '--color-primary-panel-text-placeholder',
      _tailwindColorsAddress: 'panel-text-placeholder'
    }
  },

  // 25) Elements_Panel_Icon
  elementsPanelIcon: {
    primary: {
      name: 'Colors/ElementsPanelIcon/Primary',
      value: {
        light: '#121B2E',
        dark: '#E7EBF1'
      },
      cssVariable: '--color-primary-panel-icon-primary',
      _tailwindColorsAddress: 'panel-icon-primary'
    },
    secondary: {
      name: 'Colors/ElementsPanelIcon/Secondary',
      value: {
        light: '#667085',
        dark: '#98A2B3'
      },
      cssVariable: '--color-primary-panel-icon-secondary',
      _tailwindColorsAddress: 'panel-icon-secondary'
    }
  },

  // 26) Elements_Panel_Border
  elementsPanelBorder: {
    base: {
      name: 'Colors/ElementsPanelBorder/Base',
      value: {
        light: '#E3E5EF',
        dark: '#384150'
      },
      cssVariable: '--color-primary-panel-line-base',
      _tailwindColorsAddress: 'panel-line-base'
    }
  },

  // 27) Elements_Panel_BG
  elementsPanelBg: {
    default: {
      name: 'Colors/ElementsPanelBg/Default',
      value: {
        light: '#EFF0F6',
        dark: '#252B35'
      },
      cssVariable: '--color-primary-panel-background-default',
      _tailwindColorsAddress: 'panel-background-default'
    },
    constant: {
      name: 'Colors/ElementsPanelBg/Constant',
      value: {
        light: '#F8F8FB',
        dark: '#2D3541'
      },
      cssVariable: '--color-primary-panel-background-constant',
      _tailwindColorsAddress: 'panel-background-constant'
    },
    hover: {
      name: 'Colors/ElementsPanelBg/Hover',
      value: {
        light: '#FCFCFD',
        dark: '#353E4D'
      },
      cssVariable: '--color-primary-panel-background-hover',
      _tailwindColorsAddress: 'panel-background-hover'
    },
    active: {
      name: 'Colors/ElementsPanelBg/Active',
      value: {
        light: '#FFFFFF',
        dark: '#3C4757'
      },
      cssVariable: '--color-primary-panel-background-active',
      _tailwindColorsAddress: 'panel-background-active'
    }
  },

  // 28) Elements_Chat_Inverse
  elementsChatInverse: {
    lightPrimary: {
      name: 'Colors/ElementsChatInverse/LightPrimary',
      value: {
        light: '#FFFFFF',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    },
    darkPrimary: {
      name: 'Colors/ElementsChatInverse/DarkPrimary',
      value: {
        light: '#121B2E',
        dark: '#E7EBF1'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    },
    darkSecondary: {
      name: 'Colors/ElementsChatInverse/DarkSecondary',
      value: {
        light: '#667085',
        dark: '#98A2B3'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    }
  },

  // 29) Elements_Chat_Reaction_Outcoming
  elementsChatReactionOutcoming: {
    default: {
      name: 'Colors/ElementsChatReactionOutcoming/Default',
      value: {
        light: '#B9D7FF',
        dark: '[MISSED]'
      },
      cssVariable: '--color-primary-reaction-hover',
      _tailwindColorsAddress: '[MISSED]'
    },
    pressed: {
      name: 'Colors/ElementsChatReactionOutcoming/Pressed',
      value: {
        light: '#5E9EFF',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    }
  },

  // 30) Elements_Chat_Reaction_Incoming
  elementsChatReactionIncoming: {
    default: {
      name: 'Colors/ElementsChatReactionIncoming/Default',
      value: {
        light: '#EFF0F6',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    },
    pressed: {
      name: 'Colors/ElementsChatReactionIncoming/Pressed',
      value: {
        light: '#3F8BFD',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    }
  },

  // 31) Elements_Chat_Bubble
  elementsChatBubble: {
    incoming: {
      name: 'Colors/ElementsChatBubble/Incoming',
      value: {
        light: '#FFFFFF',
        dark: '#FFFFFF'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    },
    outcomung: {
      name: 'Colors/ElementsChatBubble/Outcomung',
      value: {
        light: '#D1E4FF',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    }
  },

  // 32) Scroll
  scroll: {
    base: {
      name: 'Colors/Scroll/Base',
      value: {
        light: '#B1BACD',
        dark: '#3C4757'
      },
      cssVariable: '--color-primary-scroll-base',
      _tailwindColorsAddress: 'primary.scroll-base'
    },
    hover: {
      name: 'Colors/Scroll/Hover',
      value: {
        light: '#9CA8B5',
        dark: '[MISSED]'
      },
      cssVariable: '[MISSED]',
      _tailwindColorsAddress: '[MISSED]'
    }
  },

  // 33) Opacity
  opacity: {
    invert: {
      name: 'Colors/Opacity/Invert',
      value: {
        light: 'rgba(255,255,255,0.4)',
        dark: '#ffffff66'
      },
      cssVariable: '--color-primary-opacity-invert-40',
      _tailwindColorsAddress: 'primary.opacity-invert-40'
    },
    gray: {
      name: 'Colors/Opacity/Gray',
      value: {
        light: '#66708566',
        dark: '#66708566'
      },
      cssVariable: '--color-primary-opacity-gray-40',
      _tailwindColorsAddress: 'primary.opacity-gray-40'
    }
  },

  // 34) Overlay
  overlay: {
    base: {
      name: 'Colors/Overlay/Base',
      value: {
        light: 'rgba(0,0,0,0.65)',
        dark: 'rgba(0,0,0,0.65)'
      },
      cssVariable: '--color-background-overlay',
      _tailwindColorsAddress: '[MISSED]'
    }
  }
}
